/**
 * Elective Courses catalog — the 6-week applied-learning sprints (Read → Understand → Do).
 * Browse courses, see access/progress, unlock via standalone ($149) or Tier-2 add-on ($49/mo).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Target, Briefcase, Video, BookOpen, Brain, Rocket, Lock, CheckCircle2, ArrowRight, Award, GraduationCap } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const BADGE_ICON = { target: Target, briefcase: Briefcase, video: Video };
const PHASE_ICON = { 1: BookOpen, 2: Brain, 3: Rocket };

const ElectiveCourses = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/academy/electives', { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/academy/login'); return; }
      toast.error('Could not load electives');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const checkout = async (courseId, mode) => {
    setBuying(`${courseId}-${mode}`);
    try {
      const r = await api.post('/academy/elective-courses/checkout', { course_id: courseId, mode, origin_url: window.location.origin }, { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBuying(''); }
  };

  const open = (courseId) => navigate(`/academy/elective-courses/${courseId}`);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
  );

  return (
    <div className="min-h-screen text-white pt-16" style={{ backgroundColor: '#050508' }} data-testid="elective-courses-page">
      {/* Hero */}
      <div className="relative py-14 px-6 border-b-2 border-zinc-900" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(249,115,22,1) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,1) 1px, transparent 1px)', backgroundSize: '54px 54px' }} />
        <div className="relative container mx-auto max-w-5xl">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-orange-500 font-black"><GraduationCap className="w-3.5 h-3.5" /> Elective Academy</div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mt-2">6-Week <span className="text-orange-500">Applied Electives</span></h1>
          <p className="text-zinc-400 text-sm max-w-2xl mt-3">Read &rarr; Understand &rarr; Do. Each elective is a six-week sprint that ends with a real capstone, an official certificate, and a badge stamp on your Scholar Card.</p>
          {(data?.badges?.length > 0 || data?.credits?.length > 0) && (
            <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="elective-earned-summary">
              {data.badges.map((b) => (
                <div key={b.key} className="flex items-center gap-1.5 border-2 border-amber-500/50 bg-amber-500/10 px-3 py-1.5" data-testid={`earned-badge-${b.key}`}>
                  <Award className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] font-black uppercase tracking-widest text-amber-300">{b.name}</span>
                </div>
              ))}
              {data.credits?.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-400"><span className="font-black text-white">{data.credits.reduce((s, c) => s + (c.amount || 0), 0)}</span> Elective Credits earned</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Courses */}
      <div className="container mx-auto max-w-5xl px-6 py-12 space-y-8">
        {data?.courses?.map((c) => {
          const BadgeIcon = BADGE_ICON[c.badge?.icon] || Award;
          const done = c.progress?.completed;
          return (
            <div key={c.id} className="border-2 border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`elective-card-${c.id}`} style={{ borderTopColor: c.accent, borderTopWidth: 3 }}>
              <div className="grid md:grid-cols-3">
                {/* Left: info */}
                <div className="md:col-span-2 p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-black text-white leading-tight">{c.title}</h2>
                      <p className="text-sm mt-1" style={{ color: c.accent }}>{c.tagline}</p>
                    </div>
                    {done && <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-400 border-2 border-emerald-500/40 px-2 py-1 whitespace-nowrap"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</span>}
                  </div>
                  <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{c.description}</p>

                  {/* 3-phase strip */}
                  <div className="grid grid-cols-3 gap-2 mt-5">
                    {c.phases.map((p) => {
                      const PI = PHASE_ICON[p.phase] || BookOpen;
                      return (
                        <div key={p.phase} className="border border-zinc-800 bg-black/40 p-3">
                          <PI className="w-4 h-4" style={{ color: c.accent }} />
                          <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mt-2">{p.weeks}</div>
                          <div className="text-xs font-bold text-white mt-0.5">{p.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* progress */}
                  {c.access && c.progress?.status !== 'not_started' && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1"><span>Progress</span><span>{c.progress.pct}%</span></div>
                      <div className="h-1.5 bg-zinc-900"><div className="h-full" style={{ width: `${c.progress.pct}%`, backgroundColor: c.accent }} /></div>
                    </div>
                  )}
                </div>

                {/* Right: badge + CTA */}
                <div className="border-t-2 md:border-t-0 md:border-l-2 border-zinc-800 p-6 flex flex-col justify-between bg-black/30">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center border-2" style={{ borderColor: c.accent, backgroundColor: `${c.accent}22` }}>
                        <BadgeIcon className="w-5 h-5" style={{ color: c.accent }} />
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Earn the badge</div>
                        <div className="text-xs font-black text-white leading-tight">{c.badge?.name}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-3">+{c.credit_amount} {c.credit_type} Credit on your transcript</div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {c.access ? (
                      <button onClick={() => open(c.id)} className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-4 py-3 text-[11px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-open-${c.id}`}>
                        {done ? 'Review Course' : c.progress?.status === 'not_started' ? 'Start Course' : 'Continue'} <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500"><Lock className="w-3 h-3" /> Locked</div>
                        <button onClick={() => checkout(c.id, 'standalone')} disabled={buying === `${c.id}-standalone`} className="w-full flex items-center justify-center gap-2 border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-buy-standalone-${c.id}`}>
                          {buying === `${c.id}-standalone` ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Unlock · ${data.pricing.standalone}</>}
                        </button>
                        <button onClick={() => checkout(c.id, 'addon')} disabled={buying === `${c.id}-addon`} className="w-full flex items-center justify-center gap-2 border-2 border-zinc-700 text-zinc-300 hover:border-white px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-buy-addon-${c.id}`}>
                          {buying === `${c.id}-addon` ? <Loader2 className="w-4 h-4 animate-spin" /> : <>All Electives Add-On · ${data.pricing.addon_monthly}/mo</>}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <p className="text-center text-[11px] text-zinc-600 pt-4">Included free with the <span className="text-zinc-400 font-bold">Live Classroom Hybrid Stream</span> tier · Add-on to <span className="text-zinc-400 font-bold">Full Virtual Private School</span> for ${data?.pricing?.addon_monthly}/mo</p>
      </div>
    </div>
  );
};

export default ElectiveCourses;
